"use client";

import UserAvatar from "components/User/UserAvatar";
import { UserTooltip } from "components/ui/v2";
import SVG from "components/ui/v2/svg-v2";
import { ICONS_MODULES_PATHS } from "components/Layout/Sidebar/components/body/content/utils/iconModulesPath";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import useTranslation from "next-translate/useTranslation";
import { EModalExploreType } from "types/Explorer.types";
import { IUser } from "types/User.types";
import { formatUserDisplayName } from "utils/string";

interface Props {
  data: IUser;
  quantity: number | undefined;
  type: string;
  compact?: boolean;
}

export const UserItemRanking = ({
  data,
  quantity,
  type,
  compact = false,
}: Props) => {
  const { t } = useTranslation("common");
  const { user: storeUser } = useUserStoreContext((store) => ({
    user: store.user,
  }));

  const iconSrc =
    type === EModalExploreType.RECOGNITION
      ? ICONS_MODULES_PATHS.CONFIG.CORPORATE_VALUES
      : type === EModalExploreType.COINS
        ? ICONS_MODULES_PATHS.MARKETPLACE.COINS_MODULE
        : "";

  if (!data) return null;

  const displayName = formatUserDisplayName(data.name, data.lastname);
  const vendorId = storeUser?.selectedVendor?.id;
  const roleName = data.role?.name;

  const badge = (
    <div
      className={
        compact
          ? "flex shrink-0 items-center gap-1 rounded-full border border-slate-200/50 bg-slate-100/80 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:border-zinc-700/50 dark:bg-zinc-800/80 dark:text-zinc-300"
          : "flex shrink-0 items-center gap-1 rounded-full border border-slate-200/50 bg-slate-100/80 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:border-zinc-700/50 dark:bg-zinc-800/80 dark:text-zinc-300"
      }
    >
      {iconSrc ? (
        <SVG src={iconSrc} width={compact ? 12 : 12} height={compact ? 12 : 12} />
      ) : null}
      <span>{quantity ?? 0}</span>
    </div>
  );

  if (compact) {
    return (
      <div className="flex w-full items-center gap-2 rounded-lg px-1 py-1.5">
        {badge}
        <UserAvatar user={data} size={32} />
        <div className="min-w-0 flex-1 leading-tight">
          <p className="truncate text-[13px] font-medium capitalize text-slate-900 dark:text-zinc-50">
            {displayName.toLowerCase()}
          </p>
          {data.email ? (
            <p className="truncate text-[9px] text-slate-500 dark:text-zinc-400">
              {data.email}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  const secondaryLine = [
    data.email,
    (roleName ?? t("Ciudadano")).toLowerCase(),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex items-center gap-2 rounded-lg px-1 py-1.5 sm:px-0.5">
      {badge}
      <div className="shrink-0">
        <UserAvatar user={data} size={32} />
      </div>
      <UserTooltip user={data as Partial<IUser>} side="top">
        <div className="min-w-0 flex-1 text-left leading-tight">
          <a
            href={`/profile/${data.id}?vendorId=${vendorId ?? ""}`}
            target="_blank"
            rel="noreferrer"
            className="block truncate text-[13px] font-medium leading-snug capitalize text-slate-900 dark:text-zinc-50"
          >
            {displayName.toLowerCase()}
          </a>
          {secondaryLine ? (
            <p className="truncate text-[9px] leading-snug text-slate-500 dark:text-zinc-400">
              {secondaryLine}
            </p>
          ) : null}
        </div>
      </UserTooltip>
    </div>
  );
};

export default UserItemRanking;
